import { useTranslation } from 'react-i18next';
import { Box, Flex, Heading, Text, Dialog, IconButton, Button } from '@radix-ui/themes';
import { CloseIcon } from 'shared/icons';
import { User } from 'user';
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useApplyJobCtx } from '../apply-job-provider';
import { MissingFieldsForm } from './missing-fields-form';
import Swal from 'sweetalert2';

function ApplyWithoutResumeView({
   jobTitle,
   jobId,
   user,
   onlyResume,
   onlyQuestionnaire,
   subscribeAlert,
   message,
   onBack,
   onApply,
}: {
   jobTitle: string;
   jobId: number;
   user: User;
   message: string
   onlyResume: unknown;
   onlyQuestionnaire: unknown;
   subscribeAlert: ReactNode;
   onBack: () => void;
   onApply: (isMissingFields: boolean) => void;
}) {

   const applyJobStore = useApplyJobCtx();
   const store = applyJobStore.withoutResumeStore;
   
   const { t } = useTranslation('global');

   const renderEmail = () => {
      if (!user.email_address) {
         return null;
      }
      if (user.email_address.includes('@email.grc.ua')) {
         return null;
      }
      return user.email_address;
   };

   const isEmptyRequredFields = !user.birthday && !user.city_id;

   function disabledSubmit() {
      if (isEmptyRequredFields) {
         if (store.form.controls['birthday'].value && store.form.controls['city_id'].value) {
            return undefined;
         }
         return true;
      } else {
         return undefined;
      }
   }

   function renderForm() {
      return isEmptyRequredFields ? <MissingFieldsForm store={store} /> : null;
   }

   return (
      <>
         <Flex mb="4" width="100%" justify="between">
            <Box>
               <Heading as="h2" mb="1" weight="medium">
                  {t('jobDescription.jobApplyTitle')}
               </Heading>
               <Text color="indigo" highContrast size="3">
                  {jobTitle}
               </Text>
            </Box>

            <Dialog.Close>
               <IconButton size="1" color="blue" highContrast variant="soft" radius="full">
                  <CloseIcon />
               </IconButton>
            </Dialog.Close>
         </Flex>

         <Box mb="3">
            <Flex gap="1">
               <Text weight="medium">{user.first_name}</Text>
               <Text weight="medium">{user.last_name}</Text>
            </Flex>
            <p className="fs-6 text-muted">{renderEmail()}</p>
            <p className="fs-6 text-muted">{user.contact}</p>
            {renderForm()}
         </Box>

         <Box my="6">{subscribeAlert}</Box>
         <div className="dataProcessingConsentBox">
            <p>
               {t('jobDescription.bottomTxt1')}{' '}
               <Link to="/data-processing-consent">{t('jobDescription.bottomTxt2')}</Link>
            </p>
         </div>
         <div className="d-flex justify-content-evenly">
            {!onlyQuestionnaire ||
               (!!onlyResume && (
                  <Button
                     variant="surface"
                     radius="small"
                     color="indigo"
                     highContrast
                     type="button"
                     size="4"
                     onClick={onBack}
                  >
                     {t('jobDescription.backButton')}
                  </Button>
               ))}
            <Button
               type="submit"
               radius="small"
               disabled={disabledSubmit()}
               loading={applyJobStore.submitting}
               size="4"
               onClick={() => {
                  applyJobStore.apply(
                     {
                        variant: 'no-resume',
                        idJob: jobId,
                        first_name: user?.first_name,
                        last_name: user?.last_name,
                        email_address: user?.email_address,
                        contact: user?.contact,
                        city_id: user?.city_id,
                        birthday: user?.birthday,
                        subscribeAlert: true,
                     },
                     {
                        t,
                        onSuccess: () => {
                           onApply(isEmptyRequredFields);
                        },
                        onError: () => {
                           const Toast = Swal.mixin({
                              toast: true,
                              timer: 5000,
                              position: 'top-end',
                              timerProgressBar: true,
                              showConfirmButton: false,
                              didOpen: (toast) => {
                                 toast.addEventListener('mouseenter', Swal.stopTimer);
                                 toast.addEventListener('mouseleave', Swal.resumeTimer);
                              },
                           });

                           Toast.fire({
                              icon: 'error',
                              title: t('jobDescription.applyError'),
                           });
                        },
                     }
                  );
               }}
            >
               {t('jobDescription.applyNow')}{' '}
            </Button>
         </div>
      </>
   );
}

export const ApplyWithoutResume = observer(ApplyWithoutResumeView);
